import ConfirmationDialog from '@/components/@material-extend/ConfirmationDialog';
import PulsatingDots from '@/components/PulsatingDots';
import useStore from '@/store/use-store';
import plusFill from '@iconify/icons-eva/plus-fill';
import { Icon } from '@iconify/react';
// material
import {
  Button,
  Card,
  Checkbox,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { sentenceCase } from 'change-case';
import { filter } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import Label from '../../components/Label';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import {
  UserListHead,
  UserListToolbar,
  UserMoreMenu
} from '../../components/_dashboard/admin/user';
import { PATH_ADMIN } from '../../routes/paths';
//

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => {
      const credentials = _user.firstName + _user.lastName + _user.email;
      return credentials.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function User() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const {
    loadingUsers,
    users,
    getUsers,
    deleteUser,
    getUserDeletionImpact,
    user: currentUser
  } = useStore((state) => state);
  const { t } = useTranslation();

  const pendingCount = users.filter((u) => !u.isActive).length;

  const togglePendingOnly = () => {
    setShowPendingOnly((prev) => !prev);
    setPage(0);
  };

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const TABLE_HEAD = [
    { id: 'firstName', label: t('first_name'), alignRight: false },
    { id: 'lastName', label: t('last_name'), alignRight: false },
    { id: 'email', label: t('email_adress'), alignRight: false },
    { id: 'isAdmin', label: t('is_admin'), alignRight: false },
    { id: 'isApi', label: t('is_api'), alignRight: false },
    { id: 'active', label: t('active'), alignRight: false },
    { id: '' }
  ];

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = users.map((n) => n.email);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, email) => {
    const selectedIndex = selected.indexOf(email);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, email);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleBulkDeleteClick = async () => {
    const selectedUsers = users.filter((u) => selected.includes(u.email));
    if (selectedUsers.length === 0) return;
    try {
      const impacts = await Promise.all(
        selectedUsers.map((u) =>
          getUserDeletionImpact(u.id)
            .then((d) => ({ u, d }))
            .catch(() => ({ u, d: null }))
        )
      );
      setBulkRows(
        impacts.map(({ u, d }) => ({
          id: u.id,
          name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
          email: u.email,
          ships: d?.totals?.ships ?? 0,
          certificates: d?.totals?.issuedCertificates ?? 0,
          isSelf: currentUser?.id === u.id
        }))
      );
      setConfirmOpen(true);
    } catch {
      toast.error(t('user.delete.impact_error'));
    }
  };

  const handleBulkConfirm = async () => {
    const deletable = bulkRows.filter((r) => !r.isSelf);
    let success = 0;
    for (const row of deletable) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await deleteUser(row.id);
        success += 1;
      } catch {
        // individual failure is reported via the aggregate toast below
      }
    }
    if (success > 0) {
      toast.success(t('user.delete.bulk_success', { count: success }));
    }
    if (bulkRows.some((r) => r.isSelf)) {
      toast.error(t('user.delete.self_error'));
    }
    if (success < deletable.length) {
      toast.error(t('user.delete.error'));
    }
    setConfirmOpen(false);
    setBulkRows([]);
    setSelected([]);
  };

  const bulkTotals = bulkRows.reduce(
    (acc, r) => ({
      ships: acc.ships + r.ships,
      certificates: acc.certificates + r.certificates
    }),
    { ships: 0, certificates: 0 }
  );

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0;

  const sortedFilteredUsers = applySortFilter(users, getComparator(order, orderBy), filterName);
  const filteredUsers = showPendingOnly
    ? sortedFilteredUsers.filter((u) => !u.isActive)
    : sortedFilteredUsers;

  const isUserNotFound = !loadingUsers && filteredUsers.length === 0;

  return (
    <Page title="Users">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            {t('users')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant={showPendingOnly ? 'contained' : 'outlined'}
              color="warning"
              onClick={togglePendingOnly}
            >
              {showPendingOnly
                ? t('user.pending_filter.show_all')
                : `${t('user.pending_filter.show_pending')} (${pendingCount})`}
            </Button>
            <Button
              variant="contained"
              component={RouterLink}
              to={PATH_ADMIN.user_create}
              startIcon={<Icon icon={plusFill} />}
            >
              {t('new_user')}
            </Button>
          </Stack>
        </Stack>

        <Card>
          <UserListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            onDelete={handleBulkDeleteClick}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={users.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const { id, email, roles, firstName, lastName, isActive } = row;
                      const isItemSelected = selected.indexOf(email) !== -1;

                      return (
                        <TableRow
                          hover
                          key={id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected}
                              onChange={(event) => handleClick(event, email)}
                            />
                          </TableCell>
                          <TableCell align="left">{firstName}</TableCell>
                          <TableCell align="left">{lastName}</TableCell>
                          <TableCell align="left">{email}</TableCell>
                          <TableCell align="left">
                            <Label
                              variant="ghost"
                              color={roles.includes('ROLE_ADMIN') ? 'success' : 'warning'}
                            >
                              {sentenceCase(roles.includes('ROLE_ADMIN') ? t('yes') : t('no'))}
                            </Label>
                          </TableCell>
                          <TableCell align="left">
                            <Label
                              variant="ghost"
                              color={roles.includes('ROLE_API') ? 'success' : 'warning'}
                            >
                              {sentenceCase(roles.includes('ROLE_API') ? t('yes') : t('no'))}
                            </Label>
                          </TableCell>
                          <TableCell align="left">
                            <Label variant="ghost" color={isActive ? 'success' : 'warning'}>
                              {sentenceCase(isActive ? t('yes') : t('no'))}
                            </Label>
                          </TableCell>
                          <TableCell align="right">
                            <UserMoreMenu
                              email={email}
                              id={id}
                              isAdmin={roles.includes('ROLE_ADMIN')}
                              isActive={isActive}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
                {loadingUsers && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper>
                          <PulsatingDots size={15} />
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={users.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        {confirmOpen && (
          <ConfirmationDialog
            open={confirmOpen}
            options={{
              title: t('user.delete.bulk_title', { count: bulkRows.length }),
              allowClose: true,
              buttonOrder: ['cancel', 'confirm'],
              cancellationText: t('cancel'),
              confirmationText: t('delete'),
              confirmationButtonProps: { color: 'error', variant: 'contained' },
              dialogProps: { maxWidth: 'sm' },
              content: (
                <>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {t('user.delete.warning')}
                  </Typography>
                  <TableContainer sx={{ mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('user.delete.user_column')}</TableCell>
                          <TableCell align="right">{t('user.delete.ships')}</TableCell>
                          <TableCell align="right">{t('user.delete.certificates')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkRows.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Typography variant="body2">{row.name || row.email}</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {row.email}
                                {row.isSelf ? ` — ${t('user.delete.self_skipped')}` : ''}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{row.ships}</TableCell>
                            <TableCell align="right">{row.certificates}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell>
                            <strong>{t('user.delete.total')}</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>{bulkTotals.ships}</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>{bulkTotals.certificates}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )
            }}
            onCancel={() => setConfirmOpen(false)}
            onClose={() => setConfirmOpen(false)}
            onConfirm={handleBulkConfirm}
          />
        )}
      </Container>
    </Page>
  );
}
